import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    resolveAIGenerationError,
    resolveAIGenerationFailureMessage,
} from '../../../../../utils/aiGenerationErrorResolver';
import { useLocation } from 'react-router-dom';
import { useMannequinContext } from '../../../context/MannequinContext';
import AIGeneratorCommandService from '../../../../../services/studio/StudioCommand/AIGenerator/AIGeneratorCommand.services';
import AIProcessLogQuery from '../../../../../services/query/AIProcessLogQuery';
import {
    parseImageUrls,
    ENABLE_HANGER_CARD_SIZE_INFO,
    resolveHangerCardModelType,
    isGeminiLikeHangerModel,
    resolveHangerCardResolution,
    resolveBaseProductBarcode,
    resolveHangerCardQualityForRequest,
} from '../AIGeneration.helper';
import {
    ensureCardGenerationNotificationPreferenceLoaded,
    getHangerCardDisplayName,
    getHangerCardGeneratedImageUrl,
    isCardGenerationNotificationsEnabled,
    showHangerCardGeneratedToast,
} from '../cardGenerationNotification';
import {
    ensureOperatorUsername,
    forceReloginForMissingUserContext,
    isUserContextMissingError,
} from '../../../../../utils/ensureOperatorUsername';
import { toAsciiSafeString } from '../../../../../utils/stringHelpers';
import LoadingGif1 from '../../../../../assets/gifs/studio/Loading_1.gif';
import LoadingGif2 from '../../../../../assets/gifs/studio/Loading_2.gif';
import LoadingGif3 from '../../../../../assets/gifs/studio/Loading_3.gif';
import LoadingGif4 from '../../../../../assets/gifs/studio/Loading_4.gif';
import LoadingGif5 from '../../../../../assets/gifs/studio/Loading_5.gif';
import LoadingGif6 from '../../../../../assets/gifs/studio/Loading_6.gif';
import LoadingGif7 from '../../../../../assets/gifs/studio/Loading_7.gif';
import LoadingGif8 from '../../../../../assets/gifs/studio/Loading_8.gif';
import LoadingGif9 from '../../../../../assets/gifs/studio/Loading_9.gif';
import LoadingGif10 from '../../../../../assets/gifs/studio/Loading_10.gif';
import LoadingGif11 from '../../../../../assets/gifs/studio/Loading_11.gif';
import LoadingGif12 from '../../../../../assets/gifs/studio/Loading_12.gif';
import LoadingGif13 from '../../../../../assets/gifs/studio/Loading_13.gif';
import LoadingGif14 from '../../../../../assets/gifs/studio/Loading_14.gif';
import LoadingGif15 from '../../../../../assets/gifs/studio/Loading_15.gif';
import LoadingGif16 from '../../../../../assets/gifs/studio/Loading_16.gif';
import LoadingGif17 from '../../../../../assets/gifs/studio/Loading_17.gif';
import LoadingGif18 from '../../../../../assets/gifs/studio/Loading_18.gif';
import LoadingGif19 from '../../../../../assets/gifs/studio/Loading_19.gif';
import LoadingGif20 from '../../../../../assets/gifs/studio/Loading_20.gif';
import LoadingGif21 from '../../../../../assets/gifs/studio/Loading_21.gif';
import LoadingGif22 from '../../../../../assets/gifs/studio/Loading_22.gif';
import LoadingGif23 from '../../../../../assets/gifs/studio/Loading_23.gif';

const MAX_POLLING_ATTEMPTS = 300;
const POLLING_INTERVAL = 8000;
const RECOVERY_WINDOW_MS = 30 * 60 * 1000;

const getFirstImageFileName = (photos: { name?: string; file?: File | null }[]): string => {
    if (!photos?.length) {
        return '';
    }
    const raw = photos[0]?.name ?? photos[0]?.file?.name ?? '';
    if (!raw) {
        return '';
    }
    const normalized = raw.replace(/\\/g, '/');
    const segments = normalized.split('/');
    return segments[segments.length - 1] ?? '';
};

// Loading GIFs array
const LOADING_GIFS = [
    LoadingGif1, LoadingGif2, LoadingGif3, LoadingGif4, LoadingGif5, LoadingGif6, LoadingGif7, LoadingGif8,
    LoadingGif9, LoadingGif10, LoadingGif11, LoadingGif12, LoadingGif13, LoadingGif14, LoadingGif15, LoadingGif16,
    LoadingGif17, LoadingGif18, LoadingGif19, LoadingGif20, LoadingGif21, LoadingGif22, LoadingGif23
];

export const useHangerGeneration = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { hangerCards, updateHangerCard } = useMannequinContext();
    const [loadingGifMap, setLoadingGifMap] = useState<{ [key: string]: string }>({});
    const hangerCardsRef = useRef(hangerCards);
    hangerCardsRef.current = hangerCards;

    // Read generation type code from URL query parameter
    const queryParams = new URLSearchParams(location.search);
    const generationTypeCode = queryParams.get('type') || undefined;

    const getRandomLoadingGif = () => {
        const randomBuffer = new Uint32Array(1);
        globalThis.crypto.getRandomValues(randomBuffer);
        const randomIndex = Math.floor((randomBuffer[0] / (0xffffffff + 1)) * LOADING_GIFS.length);
        return LOADING_GIFS[randomIndex];
    };

    const notifyCardGenerationSuccess = useCallback(
        async (cardId: string, imageUrl?: string) => {
            await ensureCardGenerationNotificationPreferenceLoaded();

            if (!isCardGenerationNotificationsEnabled()) {
                return;
            }

            const cards = hangerCardsRef.current;
            const cardIndex = cards.findIndex((item) => item.id === cardId);
            const card = cards[cardIndex];

            if (!card) {
                return;
            }

            showHangerCardGeneratedToast({
                cardId,
                cardLabel: getHangerCardDisplayName(card, cardIndex, t),
                titleText: t('mannequin.aiGeneration.hangerFlow.uploadGenerate.generatedToastTitle'),
                hintText: t('mannequin.aiGeneration.hangerFlow.uploadGenerate.generatedToastHint'),
                imageUrl: imageUrl ?? getHangerCardGeneratedImageUrl(card),
            });
        },
        [t]
    );

    const pollCardStatus = async (cardId: string, predictionId: string, outputFormat: string) => {
        console.log(`🔍 Starting polling for card ${cardId} with predictionId: ${predictionId}`);
        let attempts = 0;

        const poll = async (): Promise<void> => {
            try {
                attempts++;
                const calculatedProgress = Math.min(20 + (attempts / MAX_POLLING_ATTEMPTS) * 70, 90);

                updateHangerCard(cardId, {
                    pollingAttempts: attempts,
                    progress: calculatedProgress,
                });

                const statusResponse = await AIGeneratorCommandService.GetGenerationStatus(predictionId);

                console.log(`📡 [Attempt ${attempts}] Status check for card ${cardId}:`, statusResponse);

                if (!statusResponse || !statusResponse.success) {
                    throw new Error(statusResponse?.errorMessage || t('mannequin.aiGeneration.errors.statusCheckFailed'));
                }

                if (statusResponse.isComplete) {
                    const isSuccess =
                        statusResponse.status?.toLowerCase() === 'succeeded' ||
                        statusResponse.status?.toLowerCase() === 'success' ||
                        statusResponse.status?.toLowerCase() === 'completed';

                    if (isSuccess && statusResponse.outputUrls && statusResponse.outputUrls.length > 0) {
                        updateHangerCard(cardId, {
                            generationStatus: 'success',
                            progress: 100,
                            generatedResults: [{
                                originalPhotoId: 'all',
                                generatedUrls: [statusResponse.outputUrls[0]],
                                outputFormat,
                            }],
                        });
                        notifyCardGenerationSuccess(cardId, statusResponse.outputUrls[0]);
                        return;
                    } else {
                        throw new Error(resolveAIGenerationError(statusResponse.errorCode));
                    }
                }

                if (attempts >= MAX_POLLING_ATTEMPTS) {
                    throw new Error(t('mannequin.aiGeneration.summary.timeout'));
                }

                await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
                return poll();
            } catch (error: any) {
                console.error(`❌ Polling error for card ${cardId}:`, error);
                updateHangerCard(cardId, {
                    generationStatus: 'error',
                    errorMessage: error.message || t('mannequin.aiGeneration.errors.statusCheckFailed'),
                });
                throw error;
            }
        };

        await poll();
    };

    const tryRecoverFromHistory = async (cardId: string, barcode: string, prompt: string): Promise<boolean> => {
        try {
            const response = await AIProcessLogQuery.GetUserHistory({
                pageNumber: 1,
                pageSize: 5,
                barcode: barcode.trim(),
            });

            const items = response.items || response.data || [];
            const successItem = items.find((item) => {
                const status = item.status?.toLowerCase();
                const isSuccess = status === 'success' || status === 'succeeded' || status === 'completed';
                if (!isSuccess || !item.outputUrl) {
                    return false;
                }

                const completedAt = new Date(item.completedDate || item.createdDate).getTime();
                if (Number.isNaN(completedAt) || Date.now() - completedAt > RECOVERY_WINDOW_MS) {
                    return false;
                }

                if (prompt?.trim() && item.prompt?.trim() && item.prompt.trim() !== prompt.trim()) {
                    return false;
                }

                return true;
            });

            if (!successItem) {
                return false;
            }

            const outputUrls = parseImageUrls(successItem.outputUrl);
            const generatedUrls = outputUrls.length > 0 ? outputUrls : [successItem.outputUrl];

            updateHangerCard(cardId, {
                generationStatus: 'success',
                progress: 100,
                predictionId: successItem.predictionId,
                errorMessage: undefined,
                generatedResults: [{
                    originalPhotoId: 'all',
                    generatedUrls,
                    outputFormat: successItem.outputFormat || undefined,
                }],
            });

            return true;
        } catch (error) {
            console.warn(`Failed to recover generation from history for card ${cardId}:`, error);
            return false;
        }
    };

    const resumeGenerationsAfterReload = useCallback(async () => {
        const cards = hangerCardsRef.current.filter((card) => card.generationStatus === 'loading');

        for (const card of cards) {
            setLoadingGifMap((prev) => ({
                ...prev,
                [card.id]: prev[card.id] || getRandomLoadingGif(),
            }));

            const recovered = card.config.barcode?.trim()
                ? await tryRecoverFromHistory(card.id, card.config.barcode, card.config.prompt)
                : false;

            if (!recovered) {
                updateHangerCard(card.id, {
                    generationStatus: 'interrupted',
                    progress: 0,
                    errorMessage: t('mannequin.aiGeneration.hangerFlow.uploadGenerate.generationInterrupted'),
                });
            }
        }
    }, [t, updateHangerCard]);

    const handleGenerateCard = async (cardId: string) => {
        const card = hangerCards.find((c) => c.id === cardId);
        if (!card) {
            return;
        }

        if (!card.config.barcode.trim()) {
            const errorMsg = t('mannequin.aiGeneration.configuration.barcodeRequired');
            updateHangerCard(cardId, { errorMessage: errorMsg });
            return;
        }

        if (!card.config.prompt.trim()) {
            const errorMsg = t('mannequin.aiGeneration.configuration.promptRequired');
            updateHangerCard(cardId, { errorMessage: errorMsg });
            return;
        }

        const userResult = await ensureOperatorUsername();
        if (!userResult.ok) {
            return;
        }

        updateHangerCard(cardId, { errorMessage: undefined });

        // GIF'i her zaman ata (stale closure sorununu önlemek için)
        setLoadingGifMap(prev => ({ ...prev, [cardId]: prev[cardId] || getRandomLoadingGif() }));

        updateHangerCard(cardId, {
            generationStatus: 'loading',
            progress: 0,
            pollingAttempts: 0,
            errorMessage: undefined,
            generatedResults: [],
        });

        try {
            const imageUrls = card.photos.map((photo) => photo.preview);
            updateHangerCard(cardId, { progress: 10 });

            const sizeBlock = ENABLE_HANGER_CARD_SIZE_INFO
                ? card.config.sizeMeasurementText?.trim()
                : undefined;
            const composedPrompt = sizeBlock
                ? `${card.config.prompt.trim()}\n\nBEDEN BİLGİLERİ\n${sizeBlock}`
                : card.config.prompt;

            const generationOutputFormat = card.config.outputFormat || 'jpeg';

            const requestData: any = {
                prompt: composedPrompt,
                imageUrls: imageUrls,
                numImages: 1,
                aspectRatio: card.config.aspectRatio,
                outputFormat: generationOutputFormat,
                resolution: resolveHangerCardResolution(card.selectedModel, card.config.resolution),
                quality: resolveHangerCardQualityForRequest(card.selectedModel, card.config.quality),
                productBarcode: toAsciiSafeString(resolveBaseProductBarcode(card.config.barcode || '')),
                fileName: toAsciiSafeString(getFirstImageFileName(card.photos)),
                modelType: resolveHangerCardModelType(card.selectedModel),
                generationTypeCode: generationTypeCode,
            };

            // Placeholder asla gönderilmez; BFF auth context'ten de çözebilir
            if (userResult.username) {
                requestData.username = userResult.username;
            }

            if (isGeminiLikeHangerModel(card.selectedModel) || card.selectedModel === 'AzureOpenAIGpt' || card.selectedModel === 'AIGate' || card.selectedModel === 'AIGateProLcw') {
                if (card.photos.length > 0 && card.photos[0].preview) {
                    const base64Data = card.photos[0].preview.split(',')[1];
                    requestData.base64Image = base64Data;
                    const mimeTypeMatch = card.photos[0].preview.match(/data:([^;]+);/);
                    requestData.mimeType = mimeTypeMatch ? mimeTypeMatch[1] : 'image/jpeg';
                }
            }

            const generateResponse = await AIGeneratorCommandService.GeneratePhoto(requestData);

            console.log(`📩 Generate response received for card ${cardId}:`, generateResponse);

            if (!generateResponse || !generateResponse.success) {
                if (isUserContextMissingError(generateResponse?.errorCode)) {
                    await forceReloginForMissingUserContext();
                    return;
                }
                throw new Error(
                    resolveAIGenerationFailureMessage(
                        generateResponse?.errorCode,
                        generateResponse?.errorMessage
                    )
                );
            }

            if (isGeminiLikeHangerModel(card.selectedModel) || card.selectedModel === 'AzureOpenAIGpt' || card.selectedModel === 'AIGate' || card.selectedModel === 'AIGateProLcw') {
                if (generateResponse.outputUrls && generateResponse.outputUrls.length > 0) {
                    updateHangerCard(cardId, {
                        generationStatus: 'success',
                        progress: 100,
                        predictionId: generateResponse.predictionId,
                        generatedResults: [{
                            originalPhotoId: 'all',
                            generatedUrls: generateResponse.outputUrls,
                            outputFormat: generationOutputFormat,
                        }],
                    });
                    notifyCardGenerationSuccess(cardId, generateResponse.outputUrls[0]);
                } else {
                    throw new Error(t('mannequin.aiGeneration.errors.generationFailed'));
                }
            } else {
                if (!generateResponse.predictionId) {
                    throw new Error(
                        resolveAIGenerationFailureMessage(
                            generateResponse?.errorCode,
                            generateResponse?.errorMessage
                        )
                    );
                }
                updateHangerCard(cardId, {
                    predictionId: generateResponse.predictionId,
                    progress: 20,
                });
                await pollCardStatus(cardId, generateResponse.predictionId, generationOutputFormat);
            }
        } catch (error: any) {
            console.error('Generation error:', error);
            
            // If it's an Axios error (e.g. timeout, 401), error.response might not exist or might be different
            // We use error.message which might be our thrown Error above, or an Axios message
            const errorMsg = error.message || t('mannequin.aiGeneration.errors.generationFailed');
            
            updateHangerCard(cardId, {
                generationStatus: 'error',
                errorMessage: errorMsg,
            });
        }
    };

    const handleGenerateAll = async () => {
        const cardsToGenerate = hangerCards.filter(
            (card) => card.photos.length > 0 && card.generationStatus === 'idle'
        );

        if (cardsToGenerate.length === 0) return;

        const userResult = await ensureOperatorUsername();
        if (!userResult.ok) {
            return;
        }

        // Önce tüm kartlar için GIF'leri ata, ardından loading yaparak GIF'lerin UI'da hazır olmasını sağla
        setLoadingGifMap(prev => {
            const updated = { ...prev };
            cardsToGenerate.forEach((card) => {
                if (!updated[card.id]) {
                    updated[card.id] = getRandomLoadingGif();
                }
            });
            return updated;
        });

        cardsToGenerate.forEach((card) => {
            updateHangerCard(card.id, {
                generationStatus: 'loading',
                progress: 0,
                pollingAttempts: 0,
                errorMessage: undefined,
                generatedResults: [],
            });
        });

        const CONCURRENT_LIMIT = 3;
        const DELAY_BETWEEN_BATCHES = 1000;

        for (let i = 0; i < cardsToGenerate.length; i += CONCURRENT_LIMIT) {
            const batch = cardsToGenerate.slice(i, i + CONCURRENT_LIMIT);
            batch.forEach((card) => {
                handleGenerateCard(card.id).catch((error) => console.error(error));
            });
            if (i + CONCURRENT_LIMIT < cardsToGenerate.length) {
                await new Promise((resolve) => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
            }
        }
    };

    const [inpaintingCardId, setInpaintingCardId] = useState<string | null>(null);

    const handleInpaint = async (cardId: string, maskBase64: string, prompt: string, referenceBase64?: string, referenceMimeType?: string) => {
        const card = hangerCards.find((c) => c.id === cardId);
        if (!card || card.generatedResults.length === 0) return;

        const userResult = await ensureOperatorUsername();
        if (!userResult.ok) {
            return;
        }

        setInpaintingCardId(cardId);

        // Set loading GIF
        setLoadingGifMap(prev => ({ ...prev, [cardId]: prev[cardId] || getRandomLoadingGif() }));

        updateHangerCard(cardId, {
            generationStatus: 'loading',
            progress: 0,
            pollingAttempts: 0,
            errorMessage: undefined,
        });

        try {
            const sourceImageUrl = card.generatedResults[0].generatedUrls[0];

            const basePrompt = prompt || card.config.prompt;
            const sizeBlock = ENABLE_HANGER_CARD_SIZE_INFO
                ? card.config.sizeMeasurementText?.trim()
                : undefined;
            const composedPrompt = sizeBlock
                ? `${basePrompt.trim()}\n\nBEDEN BİLGİLERİ\n${sizeBlock}`
                : basePrompt;

            const generationOutputFormat = card.config.outputFormat || 'jpeg';

            const requestData: any = {
                prompt: composedPrompt,
                imageUrls: [],
                numImages: 1,
                aspectRatio: card.config.aspectRatio,
                outputFormat: generationOutputFormat,
                resolution: resolveHangerCardResolution(card.selectedModel, card.config.resolution),
                quality: resolveHangerCardQualityForRequest(card.selectedModel, card.config.quality),
                productBarcode: toAsciiSafeString(resolveBaseProductBarcode(card.config.barcode || '')),
                fileName: toAsciiSafeString(getFirstImageFileName(card.photos)),
                modelType: resolveHangerCardModelType(card.selectedModel),
                isInpainting: true,
                inpaintingSourceImageUrl: sourceImageUrl,
                maskBase64Image: maskBase64,
                maskMimeType: 'image/png',
                referenceBase64Image: referenceBase64 || '',
                referenceMimeType: referenceMimeType || 'image/jpeg',
                generationTypeCode: generationTypeCode,
            };

            if (userResult.username) {
                requestData.username = userResult.username;
            }

            updateHangerCard(cardId, { progress: 30 });

            const generateResponse = await AIGeneratorCommandService.GeneratePhoto(requestData);

            if (!generateResponse || !generateResponse.success) {
                if (isUserContextMissingError(generateResponse?.errorCode)) {
                    await forceReloginForMissingUserContext();
                    return;
                }
                throw new Error(
                    resolveAIGenerationFailureMessage(
                        generateResponse?.errorCode,
                        generateResponse?.errorMessage
                    )
                );
            }

            if (generateResponse.outputUrls && generateResponse.outputUrls.length > 0) {
                updateHangerCard(cardId, {
                    generationStatus: 'success',
                    progress: 100,
                    predictionId: generateResponse.predictionId,
                    generatedResults: [{
                        originalPhotoId: 'inpaint',
                        generatedUrls: generateResponse.outputUrls,
                        outputFormat: generationOutputFormat,
                    }],
                });
                notifyCardGenerationSuccess(cardId, generateResponse.outputUrls[0]);
            } else {
                throw new Error(t('mannequin.aiGeneration.errors.generationFailed'));
            }
        } catch (error: any) {
            console.error('Inpainting error:', error);
            
            const errorMsg = error.message || t('mannequin.aiGeneration.errors.generationFailed');
            
            updateHangerCard(cardId, {
                generationStatus: 'error',
                errorMessage: errorMsg,
            });
        } finally {
            setInpaintingCardId(null);
        }
    };

    return {
        handleGenerateCard,
        handleGenerateAll,
        handleInpaint,
        pollCardStatus,
        resumeGenerationsAfterReload,
        loadingGifMap,
        setLoadingGifMap,
        inpaintingCardId
    };
};

